/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type MatrixEvent } from "matrix-js-sdk";

/**
 * Отметка «у меня зазвонило», которую шлёт клиент того, кому звонят.
 *
 * Событие живёт в комнате рядом с `m.rtc.decline` и по той же схеме ссылается
 * на событие вызова. Клиент, который отказ уже умеет читать, читает и отметку —
 * отдельного канала и похода на бэкенд не появляется. В ленту сообщений отметка
 * не попадает: тип чужой, отрисовщика у него нет, как и у отказа.
 */
export const RingAckEventType = "klava.call.ringing";

export interface RingAckEventContent {
  version: 1;
  "m.relates_to": {
    rel_type: "m.reference";
    event_id: string;
  };
}

/**
 * Собирает содержимое отметки для конкретного события вызова.
 */
export function makeRingAckContent(
  notificationEventId: string,
): RingAckEventContent {
  return {
    version: 1,
    "m.relates_to": {
      rel_type: "m.reference",
      event_id: notificationEventId,
    },
  };
}

/**
 * Отметка засчитывается, только если её прислал кто-то другой и именно на наш
 * вызов. Своё эхо и отметки от прошлых вызовов игнорируются: иначе звонящий
 * увидел бы «звонит» там, где на той стороне ничего не происходит.
 */
export function isRingAckFor(
  event: MatrixEvent,
  notificationEventId: string,
  localUserId: string | null,
): boolean {
  if (event.getType() !== RingAckEventType) return false;

  const sender = event.getSender();
  if (!sender || sender === localUserId) return false;

  const relation = event.getRelation();
  return (
    relation?.rel_type === "m.reference" &&
    relation.event_id === notificationEventId
  );
}
